// ============================================================================
// ActivityDetailSheet — the calendar's ACTIVITY detail panel (mobile-first).
// ----------------------------------------------------------------------------
// Tapping an activity chip on either calendar (trainer all-clients or client
// own) opens this sheet. It shows the full activity: a PROVENANCE badge (coach
// homework / your own exercise), the (optional) client, type, the metric block
// (sets × reps @ weight, or duration / distance), status and notes. Read-only —
// it mirrors the SessionDetailSheet pattern but for activity_log rows.
//
// Provenance colour comes from ENTRY_STYLE (lime = own, violet = suggested) so
// it matches the chip the user tapped and the CalendarLegend. Strings live in
// the bilingual `activity` namespace. Built on the same dashboard-local Sheet.
// ============================================================================
import { useTranslation } from "react-i18next";
import { Avatar, Badge } from "../components";
import { IconCalendar, IconClock } from "../components/icons";
import { cn } from "../lib/cn";
import { formatDateTimeLong } from "../lib/date";
import { Sheet } from "./Sheet";
import { ENTRY_STYLE } from "./sessionStyle";
import type { ActivityVM } from "./viewModel";

export interface ActivityDetailSheetProps {
  /** The activity to show; null closes the sheet. */
  activity: ActivityVM | null;
  onClose: () => void;
  /** Show the client name/avatar (trainer calendar). Hidden on the client's own. */
  showClient?: boolean;
}

/** Long, locale-aware datetime for the header; falls back to the raw value. */
function fmtWhen(startsAt: string): string {
  return formatDateTimeLong(startsAt) || startsAt;
}

export function ActivityDetailSheet({
  activity,
  onClose,
  showClient = false,
}: ActivityDetailSheetProps) {
  const { t } = useTranslation("activity");
  const st = activity ? ENTRY_STYLE[activity.kind] : null;

  const metricRows: Array<{ label: string; value: string }> = [];
  if (activity) {
    if (activity.sets != null || activity.reps != null || activity.weightKg != null) {
      const parts: string[] = [];
      if (activity.sets != null) parts.push(t("detail.setsValue", { count: activity.sets }));
      if (activity.reps != null) parts.push(t("detail.repsValue", { count: activity.reps }));
      let line = parts.join(" × ");
      if (activity.weightKg != null) {
        line = line
          ? t("detail.atWeight", { line, weight: activity.weightKg })
          : t("detail.weightValue", { weight: activity.weightKg });
      }
      if (line) metricRows.push({ label: t("detail.strength"), value: line });
    }
    if (activity.durationMin != null) {
      metricRows.push({
        label: t("detail.duration"),
        value: t("detail.minutesValue", { count: activity.durationMin }),
      });
    }
    if (activity.distance != null) {
      metricRows.push({
        label: t("detail.distance"),
        value: t("detail.distanceValue", { distance: activity.distance }),
      });
    }
  }

  return (
    <Sheet
      open={activity != null}
      onClose={onClose}
      size="md"
      title={
        activity ? (
          <span className="inline-flex items-center gap-2.5">
            <span
              className={cn("h-6 w-1.5 shrink-0 rounded-pill", st?.grad)}
              aria-hidden
            />
            {activity.title || t(`type.fallback`)}
          </span>
        ) : (
          ""
        )
      }
      subtitle={activity ? fmtWhen(activity.startsAt) : undefined}
    >
      {!activity ? null : (
        <div className="space-y-4 py-1">
          {/* Provenance + status */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={st?.tone ?? "neutral"} dot>
              {t(`provenance.${activity.kind}`)}
            </Badge>
            <Badge
              tone={activity.status === "completed" ? "success" : "neutral"}
              dot
            >
              {activity.status === "completed"
                ? t("detail.statusCompleted")
                : t("detail.statusPlanned")}
            </Badge>
          </div>

          {/* Client (trainer calendar only) */}
          {showClient && activity.clientName && (
            <div className="flex items-center gap-3">
              <Avatar name={activity.clientName} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-bold text-body">
                  {activity.clientName}
                </p>
                <p className="text-sm text-body-soft">{t("detail.loggedBy")}</p>
              </div>
            </div>
          )}

          {/* Detail rows */}
          <dl className="divide-y divide-line overflow-hidden rounded-md ring-1 ring-line">
            <DetailRow
              icon={<IconCalendar width={17} height={17} />}
              label={t("detail.when")}
              value={fmtWhen(activity.startsAt)}
            />
            {activity.type && (
              <DetailRow
                icon={
                  <span className={cn("h-3 w-3 rounded-full", st?.dot)} aria-hidden />
                }
                label={t("detail.type")}
                value={activity.type}
              />
            )}
            {metricRows.map((r) => (
              <DetailRow
                key={r.label}
                icon={<IconClock width={17} height={17} />}
                label={r.label}
                value={r.value}
              />
            ))}
          </dl>

          {activity.notes && (
            <div className="rounded-md bg-surface-soft p-3 ring-1 ring-line">
              <p className="mb-1 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("detail.notes")}
              </p>
              <p className="whitespace-pre-line text-sm text-body">
                {activity.notes}
              </p>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 bg-surface px-4 py-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-surface-sunken text-body-soft">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-xs font-bold uppercase tracking-kicker text-body-faint">
          {label}
        </dt>
        <dd className="truncate font-display text-sm font-bold text-body">
          {value}
        </dd>
      </div>
    </div>
  );
}
